import { colors } from '@lmc/common/constants'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { BarChart } from '../../../../components/charts'
import {
  HeaderSection,
  NoDataFound,
  Spinner,
  Table
} from '../../../../components/generic'
import Operations from '../../../../operations'
import { GroupsSidebarNavigation } from '../../components/reports'
import {
  getHighChartsSeries,
  getTableData,
  orderByTotal
} from '../../utils/groupReports'

const renderEmpty = () => (
  <div style={{ paddingTop: 5 }}>
    <NoDataFound
      heading="Nothing to see here..."
      text="Your group has no Care Plans set up."
    />
  </div>
)

const renderLoading = () => (
  <p>
    <Spinner />
  </p>
)

export const renderContent = (data, sort, setSort) => {
  const categories = [
    {
      key: 'later',
      label: 'Over 3 months',
      color: colors.purple,
      icon: styles.headerIcons.later
    },
    {
      key: 'threeMonths',
      label: 'Next 3 months',
      color: colors.darkblue,
      icon: styles.headerIcons.threeMonths
    },
    {
      key: 'month',
      label: 'Due this month',
      color: colors.turquoise,
      icon: styles.headerIcons.month
    },
    {
      key: 'week',
      label: 'Due this week',
      color: colors.orange,
      icon: styles.headerIcons.week
    },
    {
      key: 'overdue',
      label: 'Overdue',
      color: colors.red,
      icon: styles.headerIcons.overdue
    }
  ]

  const columns = [
    {
      label: 'Site',
      sortable: false
    },
    ...categories.map((category) => ({
      label: category.label,
      key: category.key,
      type: 'percentage',
      icon: category.icon
    }))
  ]

  const sortedData = orderByTotal(data, sort)
  const series = getHighChartsSeries(sortedData, categories)

  return (
    <div style={{ paddingTop: '-30px' }}>
      <BarChart
        type="bar"
        height={200 + sortedData.length * 40}
        categories={sortedData.map((d) => (d.keyDoc ? d.keyDoc.name : d.key))}
        series={series.reverse()}
        stacked
        legend={{ reversed: true }}
        yAxisTickFormat="{value}%"
        yAxisTickInterval={25}
      />
      <Table
        data={getTableData(sortedData, categories, MAX_NAME_LENGTH)}
        defaultSorted={{ title: 'Overdue', sorted: 'desc' }}
        onSortChange={(newSort) => setSort(newSort)}
        columns={columns}
      />
    </div>
  )
}

export function GroupCarePlans(props) {
  const { fetchLists, isLoading, data } = props

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchLists()
  }, [])

  const [sort, setSort] = useState({ key: 'overdue', order: 'desc' })

  return (
    <div style={styles.mainContainer}>
      <StyledNavigation>
        <GroupsSidebarNavigation />
      </StyledNavigation>
      <StyledInnerContainer>
        <div style={{ paddingBottom: 10 }}>
          <HeaderSection title="Today's Care Plans" />
        </div>
        {isLoading
          ? renderLoading()
          : !data || !data.length
            ? renderEmpty()
            : renderContent(data, sort, setSort)}
      </StyledInnerContainer>
    </div>
  )
}

const MAX_NAME_LENGTH = 20

const styles = {
  headerIcons: {
    later: {
      name: 'tick-in-circle',
      color: colors.purple
    },
    threeMonths: {
      name: 'tick-in-circle',
      color: colors.darkblue
    },
    month: {
      name: 'tick-in-circle',
      color: colors.turquoise
    },
    week: {
      name: 'clock',
      color: colors.orange
    },
    overdue: {
      name: 'overdue',
      color: colors.red
    }
  },
  mainContainer: {
    margin: '0 auto',
    maxWidth: 2000,
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    minHeight: 'calc(100vh - 64px)'
  }
}

const StyledInnerContainer = styled.div`
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  margin-top: 24px;
  height: 100%;
  width: 100%;
`

const StyledNavigation = styled.div`
  ${({ theme }) => theme.mediaQueries?.mobileDown} {
    display: none;
  }
`

GroupCarePlans.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string
}

const sumCategories = (data, keyProp = 'key', valueProp = 'value') => {
  const totals = {}
  for (const group of data) {
    for (const item of group.data) {
      const key = item[keyProp]
      const value = Number(item[valueProp]) || 0

      totals[key] = (totals[key] || 0) + value
    }
  }

  return totals
}

const mapStateToProps = (state) => {
  const requestState = get(state, 'data.requests.group_careplans', {})
  const response = get(requestState, 'response')
  let mappedResponse

  let transformedData = null
  if (response) {
    mappedResponse = sumCategories(response)
    transformedData = [
      {
        key: 'group_summary',
        keyDoc: { name: 'Group Summary' },
        data: [
          { key: 'overdue', value: mappedResponse.overdue || 0 },
          { key: 'week', value: mappedResponse.week || 0 },
          { key: 'month', value: mappedResponse.month || 0 },
          { key: 'threeMonths', value: mappedResponse.threeMonths || 0 },
          { key: 'later', value: mappedResponse.later || 0 }
        ]
      }
    ]
  }

  return {
    isLoading: get(requestState, 'isLoading', false),
    error: get(requestState, 'error'),
    data: transformedData
  }
}

const mapDispatchToProps = (dispatch) => ({
  fetchLists: () => {
    // dispatch(Operations.loadList('homes'))
    dispatch(
      Operations.fetchData({
        url: 'aggregations/care-plans',
        query: {},
        id: 'group_careplans'
      })
    )
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(GroupCarePlans)
